import React from "react";
import { CircularProgress, Typography } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import type { PartyType } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useErrorToast } from "../hooks/useErrorToast";
import { useParty } from "../hooks/useParty";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.party`;

const Row: React.FC<{ label: string; value: string | null; testId: string }> = ({
  label,
  value,
  testId,
}) => (
  <div className="flex flex-col">
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" data-testid={testId}>
      {value ?? "-"}
    </Typography>
  </div>
);

const PartyPanel: React.FC<{
  operationId: string;
  code: string;
  type: PartyType;
  onClose: () => void;
}> = ({ operationId, code, type, onClose }) => {
  const { t } = useTranslation();
  const party = useParty(operationId, code, type, true);
  useErrorToast(party.isError, t(`${L}.load-failed`));
  const data = party.data;
  const hasContacts = Boolean(
    data && (data.contactPerson || data.email || data.mobilePhone || data.officePhone),
  );

  return (
    <SideDrawer
      isOpen
      title={t(`${L}.${type === "SHIPPER" ? "title-shipper" : "title-stock-pile"}`)}
      onClose={onClose}
      onSubmit={onClose}
      hideCancelButton
      submitLabel={t(`${L}.close`)}
    >
      <div className="w-[400px] max-w-[100vw] flex flex-col gap-3 px-6 py-2">
        <Typography variant="subtitle1" data-testid="party-panel-code">
          {code}
        </Typography>
        {party.isLoading && <CircularProgress />}
        {data && (
          <>
            <Row label={t(`${L}.long-name`)} value={data.longName} testId="party-panel-long-name" />
            {hasContacts ? (
              <>
                <Row
                  label={t(`${L}.contact-person`)}
                  value={data.contactPerson}
                  testId="party-panel-contact-person"
                />
                <Row label={t(`${L}.email`)} value={data.email} testId="party-panel-email" />
                <Row
                  label={t(`${L}.mobile-phone`)}
                  value={data.mobilePhone}
                  testId="party-panel-mobile-phone"
                />
                <Row
                  label={t(`${L}.office-phone`)}
                  value={data.officePhone}
                  testId="party-panel-office-phone"
                />
              </>
            ) : (
              <div data-testid="party-panel-contacts-unavailable">
                <Alert type="info">{t(`${L}.contacts-unavailable`)}</Alert>
              </div>
            )}
          </>
        )}
      </div>
    </SideDrawer>
  );
};

export default PartyPanel;
